/**
 * POP — fixed starting balls that split on same-size collision into smaller, faster sizes.
 * Base ladder: large → medium → small → mini (mini never splits).
 * Size has no upper cap: minis fusing into a ball keep growing it past “large”.
 * Speed is locked to size — no time or bounce acceleration.
 */

import { gameRandom } from "./game-rng";
import { getGameSimNowMs } from "./game-sim-clock";

export const POP_START_COUNT = 50;
/** Size index: … → -1 (beyond large) → 0 large → 1 medium → 2 small → 3 mini. */
export const POP_SIZE_LARGE = 0;
export const POP_SIZE_MINI = 3;
export const POP_SIZE_COUNT = 4;

/** Reference radii (px) before layout scale for the base 4 tiers. */
export const POP_RADIUS_REF = [28, 20, 14, 8] as const;
/** Extra radius (pre-scale) added per size step beyond large (size &lt; 0). */
export const POP_GROW_RADIUS_STEP = 6;
/** Base speeds per size (pre–layout-scale) for the base 4 tiers. */
export const POP_SPEED = [0.5, 0.75, 1.1, 1.5] as const;
/** Floor speed for very large (overgrown) balls. */
export const POP_MIN_SPEED = 0.22;
/** After a ball splits (and at run start), no split until this cooldown elapses. */
export const POP_SPLIT_COOLDOWN_MS = 2000;

export type PopBallLike = {
    x: number;
    y: number;
    vx: number;
    vy: number;
    r: number;
    popSize?: number;
    /** `performance.now()` timestamp when this ball may split again. */
    popSplitReadyAt?: number;
    lastBounceFx: number;
};

/** Cap at mini only — negative sizes (larger than large) are allowed. */
export function clampPopSize(size: number): number {
    const n = Math.floor(Number(size));
    if (!Number.isFinite(n)) return POP_SIZE_LARGE;
    return Math.min(POP_SIZE_MINI, n);
}

export function isPopMini(size: number): boolean {
    return clampPopSize(size) >= POP_SIZE_MINI;
}

/** Starting large tier or any overgrown size (size index ≤ large). */
export function isPopLargeOrBigger(size: number): boolean {
    return clampPopSize(size) <= POP_SIZE_LARGE;
}

/**
 * Split on contact when sizes match, or when both are large/overgrown
 * (large+ may split against any other large+ even if tiers differ).
 */
export function popSizesShouldSplit(sizeA: number, sizeB: number): boolean {
    const a = clampPopSize(sizeA);
    const b = clampPopSize(sizeB);
    if (a === b) return a < POP_SIZE_MINI;
    return isPopLargeOrBigger(a) && isPopLargeOrBigger(b);
}

export function popCanSplit(size: number): boolean {
    return clampPopSize(size) < POP_SIZE_MINI;
}

/** Size allows split and cooldown (if any) has expired. */
export function popBallReadyToSplit(ball: PopBallLike, now = getGameSimNowMs()): boolean {
    if (!popCanSplit(ball.popSize ?? POP_SIZE_LARGE)) return false;
    const readyAt = Number(ball.popSplitReadyAt) || 0;
    return now >= readyAt;
}

export function popNextSize(size: number): number | null {
    const s = clampPopSize(size);
    return s < POP_SIZE_MINI ? s + 1 : null;
}

/** One step bigger (toward / past large) when a mini fuses in. */
export function popPrevSize(size: number): number {
    return clampPopSize(size) - 1;
}

export function popRadiusPx(size: number, layoutScale: number): number {
    const s = clampPopSize(size);
    if (s >= POP_SIZE_LARGE) {
        return POP_RADIUS_REF[s]! * layoutScale;
    }
    const stepsBeyond = -s;
    return (POP_RADIUS_REF[POP_SIZE_LARGE]! + stepsBeyond * POP_GROW_RADIUS_STEP) * layoutScale;
}

export function popSpeedValue(size: number, layoutScale: number): number {
    const s = clampPopSize(size);
    if (s >= POP_SIZE_LARGE) {
        return POP_SPEED[s]! * layoutScale;
    }
    const stepsBeyond = -s;
    const spd = Math.max(POP_MIN_SPEED, POP_SPEED[POP_SIZE_LARGE]! / (1 + 0.18 * stepsBeyond));
    return spd * layoutScale;
}

/** Lock velocity magnitude to the size’s base speed (direction preserved). */
export function enforcePopBallSpeed(ball: PopBallLike, layoutScale: number): void {
    const size = clampPopSize(ball.popSize ?? POP_SIZE_LARGE);
    const target = popSpeedValue(size, layoutScale);
    const len = Math.hypot(ball.vx, ball.vy);
    if (len < 1e-6) {
        const a = gameRandom() * Math.PI * 2;
        ball.vx = Math.cos(a) * target;
        ball.vy = Math.sin(a) * target;
        return;
    }
    const scale = target / len;
    ball.vx *= scale;
    ball.vy *= scale;
}

/**
 * Grow a ball one size step after absorbing a mini (no maximum size).
 * Always changes the size index and updates radius/speed.
 */
export function growPopBall(ball: PopBallLike, layoutScale: number): boolean {
    const next = popPrevSize(ball.popSize ?? POP_SIZE_LARGE);
    ball.popSize = next;
    ball.r = popRadiusPx(next, layoutScale);
    enforcePopBallSpeed(ball, layoutScale);
    return true;
}

export type PopSplitChild = {
    size: number;
    x: number;
    y: number;
    vx: number;
    vy: number;
    r: number;
};

/**
 * Replace one ball with two of the next size, offset along the given axis.
 * `ax/ay` should be a unit vector (typically collision tangent or normal).
 */
export function makePopSplitChildren(
    ball: PopBallLike,
    ax: number,
    ay: number,
    layoutScale: number,
    canvasW: number,
    canvasH: number,
): PopSplitChild[] | null {
    const next = popNextSize(ball.popSize ?? POP_SIZE_LARGE);
    if (next == null) return null;

    let nx = ax;
    let ny = ay;
    let nLen = Math.hypot(nx, ny);
    if (nLen < 1e-6) {
        const a = gameRandom() * Math.PI * 2;
        nx = Math.cos(a);
        ny = Math.sin(a);
        nLen = 1;
    } else {
        nx /= nLen;
        ny /= nLen;
    }

    const r = popRadiusPx(next, layoutScale);
    const speed = popSpeedValue(next, layoutScale);
    const sep = Math.max(r * 1.15, 4 * layoutScale);

    const clampPos = (x: number, y: number) => ({
        x: Math.max(r, Math.min(canvasW - r, x)),
        y: Math.max(r, Math.min(canvasH - r, y)),
    });

    const a = clampPos(ball.x - nx * sep, ball.y - ny * sep);
    const b = clampPos(ball.x + nx * sep, ball.y + ny * sep);

    return [
        { size: next, x: a.x, y: a.y, vx: -nx * speed, vy: -ny * speed, r },
        { size: next, x: b.x, y: b.y, vx: nx * speed, vy: ny * speed, r },
    ];
}

/** Elastic separation + impulse for two balls that should bounce (e.g. minis). */
export function bouncePopBalls(a: PopBallLike, b: PopBallLike, layoutScale: number): void {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const dist = Math.hypot(dx, dy);
    const minDist = a.r + b.r;
    if (dist <= 0 || dist >= minDist) return;

    const nx = dx / dist;
    const ny = dy / dist;
    const overlap = (minDist - dist) / 2;
    a.x -= nx * overlap;
    a.y -= ny * overlap;
    b.x += nx * overlap;
    b.y += ny * overlap;

    const rvx = b.vx - a.vx;
    const rvy = b.vy - a.vy;
    const velAlongNormal = rvx * nx + rvy * ny;
    if (velAlongNormal > 0) {
        enforcePopBallSpeed(a, layoutScale);
        enforcePopBallSpeed(b, layoutScale);
        return;
    }

    const impulse = -velAlongNormal;
    a.vx -= impulse * nx;
    a.vy -= impulse * ny;
    b.vx += impulse * nx;
    b.vy += impulse * ny;
    enforcePopBallSpeed(a, layoutScale);
    enforcePopBallSpeed(b, layoutScale);
}

/** Push a non-splitting ball out of a splitter and reverse along the normal. */
export function bouncePopBallOffPoint(
    ball: PopBallLike,
    otherX: number,
    otherY: number,
    otherR: number,
    layoutScale: number,
): void {
    const dx = ball.x - otherX;
    const dy = ball.y - otherY;
    let dist = Math.hypot(dx, dy);
    const minDist = ball.r + otherR;
    let nx: number;
    let ny: number;
    if (dist < 1e-6) {
        const a = gameRandom() * Math.PI * 2;
        nx = Math.cos(a);
        ny = Math.sin(a);
        dist = 0;
    } else {
        nx = dx / dist;
        ny = dy / dist;
    }
    if (dist < minDist) {
        ball.x = otherX + nx * minDist;
        ball.y = otherY + ny * minDist;
    }
    const along = ball.vx * nx + ball.vy * ny;
    if (along < 0) {
        ball.vx -= 2 * along * nx;
        ball.vy -= 2 * along * ny;
    }
    enforcePopBallSpeed(ball, layoutScale);
}
